import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageSquareIcon from "@redotech/redo-icons/message-square-01.svg";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { Dispatch, SetStateAction } from "react";
import { memo } from "react";
import {
  getStaggeredContentRevealDelayMs,
  getTitleContentRevealDelayMs,
  StepMotionGroup,
  StepMotionItem,
} from "./step-motion";
import { StepTitle } from "./step-title";
import type { ConciergeEscalation } from "./types";

const ESCALATION_OPTIONS = [
  {
    escalateToSupport: true,
    title: "Hand off to my support team",
    description:
      "Commerce Agent escalates the conversation to your Redo inbox so a person can take over.",
    icon: MessageSquareIcon,
  },
  {
    escalateToSupport: false,
    title: "Point shoppers to an email",
    description:
      "Commerce Agent asks the shopper to email your support inbox and never hands off.",
    icon: MailIcon,
  },
] as const;

export interface EscalationStepProps {
  animateItems?: boolean;
  stepNumber: number;
  escalation: ConciergeEscalation;
  setEscalation: Dispatch<SetStateAction<ConciergeEscalation>>;
  emailError: string | null;
  onEmailBlur(): void;
}

export const EscalationStep = memo(function EscalationStep({
  animateItems,
  stepNumber,
  escalation,
  setEscalation,
  emailError,
  onEmailBlur,
}: EscalationStepProps) {
  const title = "When Commerce Agent can't help";
  const contentRevealDelayMs = getTitleContentRevealDelayMs(title);

  return (
    <StepMotionGroup animateItems={animateItems}>
      <Flex dir="column" gap="lg">
        <StepMotionItem animateItems={animateItems}>
          <Text fontSize="sm" textColor="tertiary">
            Step {stepNumber}
          </Text>
        </StepMotionItem>
        <StepMotionItem animateItems={animateItems}>
          <StepTitle animate={animateItems} text={title} />
        </StepMotionItem>
        <StepMotionItem
          animateItems={animateItems}
          delayMs={contentRevealDelayMs}
        >
          <Text fontSize="lg" textColor="tertiary">
            Decide what happens when a shopper needs a person. You can change
            this later in the playbook.
          </Text>
        </StepMotionItem>
        {ESCALATION_OPTIONS.map((option, index) => {
          const Icon = option.icon;
          return (
            <StepMotionItem
              animateItems={animateItems}
              delayMs={getStaggeredContentRevealDelayMs(title, index)}
              key={String(option.escalateToSupport)}
            >
              <RedoCheckboxCard
                checked={
                  escalation.escalateToSupport === option.escalateToSupport
                }
                icon={<Icon height={20} width={20} />}
                inputType="radio"
                layout={Layout.LEFT}
                onClick={() =>
                  setEscalation((current) => ({
                    ...current,
                    escalateToSupport: option.escalateToSupport,
                  }))
                }
                title={option.title}
              >
                <Text fontSize="xs" textColor="secondary">
                  {option.description}
                </Text>
              </RedoCheckboxCard>
            </StepMotionItem>
          );
        })}
        {escalation.escalateToSupport === false ? (
          <StepMotionItem animateItems={animateItems}>
            <RedoTextInput
              description="Shoppers will be redirected to a support email when Commerce Agent can't help"
              errorText={emailError ?? undefined}
              fullWidth
              label="Support email"
              onBlur={onEmailBlur}
              placeholder="support@brand.com"
              setValue={(value) =>
                setEscalation((current) => ({
                  ...current,
                  supportEmail: value,
                }))
              }
              size="md"
              state={emailError ? "error" : "default"}
              value={escalation.supportEmail}
            />
          </StepMotionItem>
        ) : null}
      </Flex>
    </StepMotionGroup>
  );
});
